import type { Facing } from "../movement/step.ts";
import { OFFSET_MAX, OFFSET_MIN, packOffsets } from "../movement/true-point.ts";
import { MAX_AWAY, MAX_STATUS_BADGE, MAX_STATUS_INFAMY, statusByte } from "./away.ts";
import { HEADER_BYTES, putHeader } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { EntityKind } from "./kinds.ts";
import { KEYFRAME_INTERVAL, MAX_U16, MAX_U32, NO_HANDLE } from "./limits.ts";
import { MAX_NAME_BYTES, sameName, type EntityName } from "./names.ts";

/**
 * The `snapshot` frame (PROTOCOL.md 5.2 and 5.3): a delta or keyframe of the entities in a client's
 * interest. The encoder writes into a caller-owned buffer and asks the map server which components are dirty for
 * each entity through a callback (the map server tracks change ticks per component once, not per client), so a
 * tick allocates nothing (ADR-030); `diffEntity` is the reference for what "dirty" means. A name travels as
 * the bytes its entity was given at join (ADR-067), copied, so no string is encoded in the tick. The decoder is
 * `snapshot-decode.ts`.
 */

/**
 * Component flags of an entity record, in the order their bytes appear.
 */
export const Component = {
  Position: 0x0001,
  FacingAnim: 0x0002,
  Health: 0x0004,
  Height: 0x0008,
  Pvp: 0x0010,
  Infamy: 0x0020,
  Buffs: 0x0040,
  Name: 0x0080,
  Level: 0x0100,
  Offsets: 0x0200,
  Companion: 0x0400,
  Spawn: 0x8000,
  All: 0x87ff,
} as const;

/**
 * The components a keyframe writes for every entity: all of them but the Name, which it writes only where a
 * delta would (ADR-067 decision 2).
 */
const KEYFRAME_COMPONENTS = Component.All & ~Component.Name;

/**
 * The components a record with Spawn may leave out: the Name, and the Offsets and the Companion, which a record of any
 * kind but a player's never carries and whose absence reads 0 (ADR-116's second amendment of 2026-10-07, ADR-142
 * decision 7.1).
 */
const OPTIONAL_WITH_SPAWN = Component.Name | Component.Offsets | Component.Companion;

/**
 * The components only a player's record carries. The Companion is also left out of a keyframe or Spawn record while its
 * three values are all 0, since a decoded absence reads 0 and those records hold a state whole; a delta carries it
 * whenever it changed, a change back to 0 included.
 */
const PLAYER_ONLY = Component.Offsets | Component.Companion;

/**
 * The most a companion id and a cloak cut take: four bits each.
 */
const MAX_NIBBLE = 15;

/**
 * The bit of a snapshot's flags byte that says a crossed list follows the removed list (PROTOCOL.md 5.2, 6.96.0): the
 * characters that left this map's stream because they crossed to its neighbour (ADR-147 decision 15).
 */
export const CROSSED_FLAG = 2;

/**
 * Header length: frame header plus tick, baseTick, ackSeq, flags, entityCount and removedCount.
 */
export const SNAPSHOT_HEADER_BYTES = HEADER_BYTES + 4 + 4 + 4 + 1 + 2 + 2;

/**
 * The bit of a snapshot's flags byte that says it is a keyframe.
 */
export const KEYFRAME_FLAG = 1;

/**
 * The most bytes one entity record takes: handle and flags, every fixed component, a buff count and 255
 * buffs, a name at both parts' bound, the level, the offsets, the companion and the spawn.
 */
export const MAX_ENTITY_BYTES = 4 + 4 + 1 + 1 + 1 + 1 + 1 + 1 + 2 * 255 + MAX_NAME_BYTES + 1 + 1 + 2 + 3;
/**
 * The largest animation state the facing byte carries (six bits), and so the largest object state index
 * a thing can be in (ADR-042): an object type has at most `MAX_ANIM + 1` states.
 */
export const MAX_ANIM = 63;
/**
 * The highest height level the Height component carries.
 */
export const MAX_HEIGHT = 3;

/**
 * The running bit of the height component's byte (ADR-116 decision 14, protocol 6.37.0): bit 7, beside the height
 * level in bits 0 and 1. Bits 2 to 6 are unused and 0.
 */
export const RUNNING_BIT = 0x80;
const MAX_BYTE = 255;

/**
 * Everything the snapshot can say about one entity. `name` is `NO_NAME` for a thing, and for a character whose
 * name the client does not hold. `level` is a character's or creature's level, 1 to 255, and 0 for a thing (ADR-074
 * decision 8) but a ground item, whose Level byte is its origin (`GroundOrigin`, 6.57.0). `infamy`, `away` and `badge` share the Infamy component's status byte (ADR-090 decision 4, ADR-112 decision 5.3): the tier, the
 * character's away state (`Away`), which a state without one reads as active, and its badge, 0 to 15, which a state without one reads as none. `height` and `running` share the Height
 * component's byte (ADR-116 decision 14): the level, 0 to 3, and whether the character is taking run steps, which a
 * state without one reads as not running; a decoded state always holds it. `offsetX` and `offsetY` are the Offsets
 * component's byte (ADR-116's amendment of 2026-10-07): how far a walking character's true point lies from its lattice
 * point on each axis, in speed units from `OFFSET_MIN` to `OFFSET_MAX`, which a state without them reads as 0 and a
 * decoded state always holds; a creature and a thing carry 0. `companion`, `cloakCut` and `cloakPalette` are the Companion
 * component's two bytes (ADR-142 decisions 7.1 and 9.7): the companion's id, 0 for none and 1 the Aethling, and the
 * cloak's cut, each 0 to 15, in the first byte's low and high four bits, and the cloak's palette, 0 to 255, in the
 * second; a state without them reads 0, a decoded state always holds them, and a creature and a thing carry 0.
 */
export interface EntityState {
  readonly handle: number;
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
  readonly anim: number;
  readonly health: number;
  readonly height: number;
  readonly running?: boolean;
  readonly pvp: number;
  readonly infamy: number;
  readonly away?: number;
  readonly badge?: number;
  readonly buffs: readonly number[];
  readonly kind: number;
  readonly appearance: number;
  readonly level: number;
  readonly offsetX?: number;
  readonly offsetY?: number;
  readonly companion?: number;
  readonly cloakCut?: number;
  readonly cloakPalette?: number;
  readonly name: EntityName;
}

/**
 * What the encoder needs for one client's snapshot. `dirty` returns the component flags to send for an
 * entity in a delta (`Component.All` for one the client's newest view lacks, 0 to omit it). A keyframe writes
 * every component but the Name for every entity whatever it returns, and the Name where it returns the Name's
 * flag, so a keyframe names only the entities a delta would (ADR-067 decision 2).
 */
export interface SnapshotInput {
  readonly tick: number;
  readonly baseTick: number;
  readonly ackSeq: number;
  readonly keyframe: boolean;
  readonly entities: readonly EntityState[];
  readonly dirty: (state: EntityState) => number;
  readonly removed: readonly number[];
  readonly crossed?: readonly number[];
}

/**
 * One decoded entity record: which components arrived and their values (absent components read 0).
 */
export interface DecodedEntity {
  readonly handle: number;
  readonly flags: number;
  readonly state: EntityState;
}

/**
 * A decoded snapshot, reused across frames on the client; entity records are rebuilt per frame.
 */
export interface SnapshotRecord {
  tick: number;
  baseTick: number;
  ackSeq: number;
  keyframe: boolean;
  entities: DecodedEntity[];
  removed: number[];
  crossed: number[];
}

/**
 * A fresh record for `decodeSnapshot` to fill.
 *
 * @returns Empty record.
 */
export function newSnapshotRecord(): SnapshotRecord {
  return { tick: 0, baseTick: 0, ackSeq: 0, keyframe: false, entities: [], removed: [], crossed: [] };
}

/**
 * The dirty flags between a base state and the current one: everything (including spawn) when the
 * client has no base for the entity, otherwise each component whose value changed.
 *
 * @param base State the client holds, or undefined.
 * @param current State now.
 * @returns Component flags.
 */
export function diffEntity(base: EntityState | undefined, current: EntityState): number {
  if (base === undefined) {
    return Component.All;
  }
  let flags = 0;
  if (base.x !== current.x || base.y !== current.y) {
    flags |= Component.Position;
  }
  if (base.facing !== current.facing || base.anim !== current.anim) {
    flags |= Component.FacingAnim;
  }
  if (base.health !== current.health) {
    flags |= Component.Health;
  }
  if (base.height !== current.height || (base.running === true) !== (current.running === true)) {
    flags |= Component.Height;
  }
  if (base.pvp !== current.pvp) {
    flags |= Component.Pvp;
  }
  if (base.infamy !== current.infamy || (base.away ?? 0) !== (current.away ?? 0) || (base.badge ?? 0) !== (current.badge ?? 0)) {
    flags |= Component.Infamy;
  }
  if (!sameBuffs(base.buffs, current.buffs)) {
    flags |= Component.Buffs;
  }
  if (!sameName(base.name, current.name)) {
    flags |= Component.Name;
  }
  if (base.level !== current.level) {
    flags |= Component.Level;
  }
  if ((base.offsetX ?? 0) !== (current.offsetX ?? 0) || (base.offsetY ?? 0) !== (current.offsetY ?? 0)) {
    flags |= Component.Offsets;
  }
  if ((base.companion ?? 0) !== (current.companion ?? 0) || (base.cloakCut ?? 0) !== (current.cloakCut ?? 0) || (base.cloakPalette ?? 0) !== (current.cloakPalette ?? 0)) {
    flags |= Component.Companion;
  }
  return flags;
}

/**
 * Whether two buff lists are equal in order.
 *
 * @param a First list.
 * @param b Second list.
 * @returns True when equal.
 */
function sameBuffs(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Whether the next snapshot for a client must be a keyframe: the interval has elapsed, or the delta's base
 * is no longer in the server's ring.
 *
 * @param tick Current tick.
 * @param lastKeyframeTick Tick of the last keyframe sent to the client.
 * @param baseTick The tick the delta would be based on.
 * @param oldestRingTick Oldest tick the ring still holds.
 * @returns True for a keyframe.
 */
export function shouldKeyframe(tick: number, lastKeyframeTick: number, baseTick: number, oldestRingTick: number): boolean {
  return tick - lastKeyframeTick >= KEYFRAME_INTERVAL || baseTick < oldestRingTick;
}

/**
 * Checks an integer field fits its width.
 *
 * @param value Value.
 * @param max Largest allowed.
 * @param what Field name for the error.
 * @throws Error when the value does not fit.
 */
function assertFits(value: number, max: number, what: string): void {
  if (!Number.isInteger(value) || value < 0 || value > max) {
    throw new Error(`${what} ${value} does not fit in 0 to ${max}`);
  }
}

/**
 * Writes a `u16` little-endian through plain byte stores, which the entity loop favours over `DataView`
 * calls because it runs for every entity of every client each tick.
 *
 * @param buffer Destination.
 * @param offset Byte offset.
 * @param value Value in 0 to 65535.
 */
function putU16(buffer: Uint8Array, offset: number, value: number): void {
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = value >>> 8;
}

/**
 * Writes a `u32` little-endian through plain byte stores, so the header needs no `DataView` either.
 *
 * @param buffer Destination.
 * @param offset Byte offset.
 * @param value Value in 0 to 4294967295.
 */
function putU32(buffer: Uint8Array, offset: number, value: number): void {
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = (value >>> 8) & 0xff;
  buffer[offset + 2] = (value >>> 16) & 0xff;
  buffer[offset + 3] = value >>> 24;
}

/**
 * Bytes one entity record takes with the given flags.
 *
 * @param flags Component flags.
 * @param buffs Buff count.
 * @param name Bytes of the Name component.
 * @returns Byte count.
 */
function entityBytes(flags: number, buffs: number, name: number): number {
  let bytes = 4;
  if (flags & Component.Position) bytes += 4;
  if (flags & Component.FacingAnim) bytes += 1;
  if (flags & Component.Health) bytes += 1;
  if (flags & Component.Height) bytes += 1;
  if (flags & Component.Pvp) bytes += 1;
  if (flags & Component.Infamy) bytes += 1;
  if (flags & Component.Buffs) bytes += 1 + 2 * buffs;
  if (flags & Component.Name) bytes += name;
  if (flags & Component.Level) bytes += 1;
  if (flags & Component.Offsets) bytes += 1;
  if (flags & Component.Companion) bytes += 2;
  if (flags & Component.Spawn) bytes += 3;
  return bytes;
}

/**
 * Whether component flags are ones a record may carry: only known components, and Spawn only with every
 * other component but the Name, which rides with it when the client lacks the name, and the Offsets and the Companion,
 * which only a player's record carries.
 *
 * @param flags Component flags.
 * @returns True when the flags are well formed.
 */
export function wellFormed(flags: number): boolean {
  return (flags & ~Component.All) === 0 && ((flags & Component.Spawn) === 0 || (flags | OPTIONAL_WITH_SPAWN) === Component.All);
}

/**
 * Encodes a snapshot into a caller-owned buffer. It allocates nothing: every field is a byte store, and a name
 * is copied from the bytes its entity holds.
 *
 * @param buffer Destination.
 * @param input Tick, base, entities and removals.
 * @returns Bytes written.
 * @throws Error when a value does not fit its field or the buffer is too small.
 */
export function encodeSnapshot(buffer: Uint8Array, input: SnapshotInput): number {
  return encodeSnapshotFrame(buffer, input, MessageType.Snapshot);
}

/**
 * Encodes the snapshot layout under a message type of its own: the `snapshot` itself, and `edgeView`, which is the same
 * header and keyframe records (PROTOCOL.md 5.24). Allocates nothing.
 *
 * @param buffer Destination.
 * @param input Tick, base, entities, removals and crossings.
 * @param type The message id written in the header.
 * @returns Bytes written.
 * @throws Error when a value does not fit its field or the buffer is too small.
 */
export function encodeSnapshotFrame(buffer: Uint8Array, input: SnapshotInput, type: number): number {
  const crossed = input.crossed;
  const crossedCount = crossed === undefined ? 0 : crossed.length;
  assertFits(input.tick, MAX_U32, "tick");
  assertFits(input.baseTick, MAX_U32, "baseTick");
  assertFits(input.ackSeq, MAX_U32, "ackSeq");
  assertFits(input.removed.length, MAX_U16, "removedCount");
  assertFits(crossedCount, MAX_U16, "crossedCount");
  if (buffer.length < SNAPSHOT_HEADER_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a snapshot header`);
  }
  putHeader(buffer, type);
  putU32(buffer, HEADER_BYTES, input.tick);
  putU32(buffer, HEADER_BYTES + 4, input.baseTick);
  putU32(buffer, HEADER_BYTES + 8, input.ackSeq);
  buffer[HEADER_BYTES + 12] = (input.keyframe ? KEYFRAME_FLAG : 0) | (crossedCount > 0 ? CROSSED_FLAG : 0);
  let offset = SNAPSHOT_HEADER_BYTES;
  let count = 0;
  const entities = input.entities;
  for (let index = 0; index < entities.length; index += 1) {
    const state = entities[index] as EntityState;
    const asked = input.keyframe ? KEYFRAME_COMPONENTS | (input.dirty(state) & Component.Name) : input.dirty(state);
    let flags = state.kind === EntityKind.Player ? asked : asked & ~PLAYER_ONLY;
    if ((flags & Component.Companion) !== 0 && (input.keyframe || (flags & Component.Spawn) !== 0) && (state.companion ?? 0) === 0 && (state.cloakCut ?? 0) === 0 && (state.cloakPalette ?? 0) === 0) {
      flags &= ~Component.Companion;
    }
    if (flags === 0) {
      continue;
    }
    if (!wellFormed(flags)) {
      throw new Error(`dirty flags ${flags} for handle ${state.handle} are not component flags`);
    }
    assertFits(state.handle, MAX_U16, "handle");
    const name = state.name.bytes;
    if ((flags & Component.Name) !== 0 && (name.length < 2 || name.length > MAX_NAME_BYTES)) {
      throw new Error(`name of ${name.length} bytes for handle ${state.handle} is not a Name component`);
    }
    if (offset + MAX_ENTITY_BYTES > buffer.length && offset + entityBytes(flags, state.buffs.length, name.length) > buffer.length) {
      throw new Error(`buffer of ${buffer.length} bytes is too small for the snapshot`);
    }
    putU16(buffer, offset, state.handle);
    putU16(buffer, offset + 2, flags);
    offset += 4;
    if (flags & Component.Position) {
      assertFits(state.x, MAX_U16, "position x");
      assertFits(state.y, MAX_U16, "position y");
      putU16(buffer, offset, state.x);
      putU16(buffer, offset + 2, state.y);
      offset += 4;
    }
    if (flags & Component.FacingAnim) {
      assertFits(state.anim, MAX_ANIM, "anim");
      buffer[offset] = state.facing | (state.anim << 2);
      offset += 1;
    }
    if (flags & Component.Health) {
      assertFits(state.health, MAX_BYTE, "health");
      buffer[offset] = state.health;
      offset += 1;
    }
    if (flags & Component.Height) {
      assertFits(state.height, MAX_HEIGHT, "height");
      buffer[offset] = state.running === true ? state.height | RUNNING_BIT : state.height;
      offset += 1;
    }
    if (flags & Component.Pvp) {
      assertFits(state.pvp, MAX_BYTE, "pvp");
      buffer[offset] = state.pvp;
      offset += 1;
    }
    if (flags & Component.Infamy) {
      const away = state.away ?? 0;
      assertFits(state.infamy, MAX_STATUS_INFAMY, "infamy");
      const badge = state.badge ?? 0;
      assertFits(away, MAX_AWAY, "away");
      assertFits(badge, MAX_STATUS_BADGE, "badge");
      buffer[offset] = statusByte(state.infamy, away, badge);
      offset += 1;
    }
    if (flags & Component.Buffs) {
      const buffs = state.buffs;
      assertFits(buffs.length, MAX_BYTE, "buff count");
      buffer[offset] = buffs.length;
      offset += 1;
      for (let b = 0; b < buffs.length; b += 1) {
        const buff = buffs[b] as number;
        assertFits(buff, MAX_U16, "buff");
        putU16(buffer, offset, buff);
        offset += 2;
      }
    }
    if (flags & Component.Name) {
      buffer.set(name, offset);
      offset += name.length;
    }
    if (flags & Component.Level) {
      assertFits(state.level, MAX_BYTE, "level");
      buffer[offset] = state.level;
      offset += 1;
    }
    if (flags & Component.Offsets) {
      const offsetX = state.offsetX ?? 0;
      const offsetY = state.offsetY ?? 0;
      assertFits(offsetX - OFFSET_MIN, OFFSET_MAX - OFFSET_MIN, "offset x");
      assertFits(offsetY - OFFSET_MIN, OFFSET_MAX - OFFSET_MIN, "offset y");
      buffer[offset] = packOffsets(offsetX, offsetY);
      offset += 1;
    }
    if (flags & Component.Companion) {
      const companion = state.companion ?? 0;
      const cut = state.cloakCut ?? 0;
      const palette = state.cloakPalette ?? 0;
      assertFits(companion, MAX_NIBBLE, "companion");
      assertFits(cut, MAX_NIBBLE, "cloak cut");
      assertFits(palette, MAX_BYTE, "cloak palette");
      buffer[offset] = companion | (cut << 4);
      buffer[offset + 1] = palette;
      offset += 2;
    }
    if (flags & Component.Spawn) {
      assertFits(state.kind, MAX_BYTE, "kind");
      assertFits(state.appearance, MAX_U16, "appearance");
      buffer[offset] = state.kind;
      putU16(buffer, offset + 1, state.appearance);
      offset += 3;
    }
    count += 1;
  }
  assertFits(count, MAX_U16, "entityCount");
  if (offset + 2 * input.removed.length > buffer.length) {
    throw new Error(`buffer of ${buffer.length} bytes is too small for the removed list`);
  }
  const removed = input.removed;
  for (let index = 0; index < removed.length; index += 1) {
    const handle = removed[index] as number;
    assertFits(handle, NO_HANDLE - 1, "removed handle");
    putU16(buffer, offset, handle);
    offset += 2;
  }
  putU16(buffer, HEADER_BYTES + 13, count);
  putU16(buffer, HEADER_BYTES + 15, input.removed.length);
  if (crossed !== undefined && crossedCount > 0) {
    if (offset + 2 + 2 * crossedCount > buffer.length) {
      throw new Error(`buffer of ${buffer.length} bytes is too small for the crossed list`);
    }
    putU16(buffer, offset, crossedCount);
    offset += 2;
    for (let index = 0; index < crossedCount; index += 1) {
      const handle = crossed[index] as number;
      assertFits(handle, NO_HANDLE - 1, "crossed handle");
      for (let r = 0; r < removed.length; r += 1) {
        if (removed[r] === handle) {
          throw new Error(`handle ${handle} is in both the removed and the crossed list`);
        }
      }
      putU16(buffer, offset, handle);
      offset += 2;
    }
  }
  return offset;
}
